import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import VerifiedBadge from "@/components/app/VerifiedBadge";
import StoriesBar from "@/components/app/StoriesBar";
import EmergencyBanner from "@/components/app/EmergencyBanner";
import MedicalDisclaimer from "@/components/app/MedicalDisclaimer";
import ClinicalCaseDialog from "@/components/app/ClinicalCaseDialog";
import ReportPostDialog from "@/components/app/ReportPostDialog";
import PushOptInBanner from "@/components/app/PushOptInBanner";
import { ThumbsUp, Heart, Lightbulb, Award, HandHeart, MessageCircle, Share2, Image as ImageIcon, Send, X, HelpCircle, FileText, Stethoscope, ShieldAlert, Flag, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const REACTIONS = [
  { key: "like", icon: ThumbsUp, label: "J'aime", color: "#618DB8" },
  { key: "love", icon: Heart, label: "Bravo", color: "#FF7A5C" },
  { key: "insight", icon: Lightbulb, label: "Instructif", color: "#FFB400" },
  { key: "celebrate", icon: Award, label: "Félicitations", color: "#10b981" },
  { key: "support", icon: HandHeart, label: "Soutien", color: "#8b5cf6" },
];
const R_BY_KEY = Object.fromEntries(REACTIONS.map((r) => [r.key, r]));

function renderContent(text) {
  // Highlight #hashtags as clickable
  const parts = text.split(/(#\w{2,30})/g);
  return parts.map((p, i) => {
    if (/^#\w+/.test(p)) {
      const tag = p.slice(1).toLowerCase();
      return (
        <a key={i} href={`/dashboard/feed?tag=${tag}`} className="text-[#618DB8] font-semibold hover:underline">{p}</a>
      );
    }
    return <span key={i}>{p}</span>;
  });
}

function timeAgo(iso) {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "à l'instant";
  if (s < 3600) return `il y a ${Math.floor(s / 60)} min`;
  if (s < 86400) return `il y a ${Math.floor(s / 3600)} h`;
  if (s < 86400 * 7) return `il y a ${Math.floor(s / 86400)} j`;
  return new Date(iso).toLocaleDateString("fr-FR");
}

function PostCard({ post, onReact, onComment }) {
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [showReactionMenu, setShowReactionMenu] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const reactionTimer = useRef(null);

  const myR = post.my_reaction ? R_BY_KEY[post.my_reaction] : null;
  const topReactions = Object.entries(post.reactions_count || {}).filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1]).slice(0, 3);

  const loadComments = async () => {
    try {
      const { data } = await api.get(`/posts/${post.post_id}/comments`);
      setComments(data);
    } catch { /* silent */ }
  };
  useEffect(() => { if (showComments) loadComments(); /* eslint-disable-next-line */ }, [showComments]);

  const submitComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    try {
      await api.post(`/posts/${post.post_id}/comment`, { content: commentText });
      setCommentText("");
      loadComments();
      onComment && onComment();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || e.message); }
  };

  return (
    <article className="bg-white rounded-2xl border border-slate-200 shadow-sm card-hover overflow-hidden" data-testid={`post-${post.post_id}`}>
      <header className="px-5 pt-5 flex items-start gap-3">
        <Link to={`/u/${post.author?.user_id}`}>
          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#618DB8] to-[#2A4365] text-white flex items-center justify-center font-bold flex-shrink-0">
            {(post.author?.name || "?").replace(/^Dr\s+/, "").charAt(0)}
          </div>
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center flex-wrap gap-1">
            <Link to={`/u/${post.author?.user_id}`} className="font-display font-bold text-slate-900 hover:text-[#618DB8]">{post.author?.name}</Link>
            {post.author?.blue_check && <VerifiedBadge size={14} />}
          </div>
          <div className="text-xs text-slate-500">{post.author?.specialty || (post.author?.role === "patient" ? "Patient" : "")} · {timeAgo(post.created_at)}</div>
        </div>
      </header>

      <div className="px-5 pt-3 pb-2 text-slate-800 whitespace-pre-wrap leading-relaxed">
        {post.type === "clinical_case" && (
          <div className="mb-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-center gap-2 text-xs font-semibold text-amber-900">
            <ShieldAlert className="h-4 w-4 text-amber-600" />
            Cas clinique · données anonymisées · validé par modération
          </div>
        )}
        {post.type === "qa" && (
          <div className="mb-3 inline-flex items-center gap-1.5 bg-blue-50 border border-blue-200 text-blue-700 rounded-full px-2.5 py-0.5 text-xs font-semibold">
            <HelpCircle className="h-3 w-3" />Question santé
          </div>
        )}
        {post.title && <div className="font-display font-bold text-lg text-deep mb-1">{post.title}</div>}
        {renderContent(post.content)}
        {post.type === "qa" && <MedicalDisclaimer inline />}
      </div>
      {post.image_url && (
        <img src={post.image_url} alt="" className="w-full max-h-[480px] object-cover bg-slate-100 border-y border-slate-100" />
      )}

      {(post.reactions_total > 0 || post.comments_count > 0) && (
        <div className="px-5 py-2 flex items-center justify-between text-xs text-slate-500 border-t border-slate-100">
          <div className="flex items-center gap-1">
            {topReactions.length > 0 && (
              <div className="flex -space-x-1">
                {topReactions.map(([k]) => {
                  const R = R_BY_KEY[k];
                  return (
                    <span key={k} className="w-5 h-5 rounded-full flex items-center justify-center border-2 border-white" style={{ background: R.color }}>
                      <R.icon className="h-3 w-3 text-white" fill="currentColor" />
                    </span>
                  );
                })}
              </div>
            )}
            {post.reactions_total > 0 && <span className="ms-1.5">{post.reactions_total}</span>}
          </div>
          {post.comments_count > 0 && (
            <button onClick={() => setShowComments((s) => !s)} className="hover:text-[#618DB8]">
              {post.comments_count} commentaire{post.comments_count > 1 ? "s" : ""}
            </button>
          )}
        </div>
      )}

      <footer className="border-t border-slate-100 px-2 py-1 flex items-center justify-around relative">
        <div className="relative"
             onMouseEnter={() => { clearTimeout(reactionTimer.current); setShowReactionMenu(true); }}
             onMouseLeave={() => { reactionTimer.current = setTimeout(() => setShowReactionMenu(false), 250); }}>
          <button onClick={() => onReact(post.post_id, myR ? myR.key : "like")} data-testid={`react-${post.post_id}`}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition flex items-center gap-2 ${myR ? "" : "text-slate-600 hover:bg-slate-100"}`}
                  style={myR ? { color: myR.color, background: myR.color + "15" } : {}}>
            {myR ? (<><myR.icon className="h-4 w-4" fill="currentColor" />{myR.label}</>) : (<><ThumbsUp className="h-4 w-4" />J'aime</>)}
          </button>
          {showReactionMenu && (
            <div className="absolute bottom-full mb-2 start-0 bg-white border border-slate-200 rounded-full shadow-lg flex gap-1 px-2 py-1.5 z-20" data-testid="reaction-picker">
              {REACTIONS.map((r) => (
                <button key={r.key} title={r.label} onClick={() => { onReact(post.post_id, r.key); setShowReactionMenu(false); }}
                        className="w-9 h-9 rounded-full flex items-center justify-center hover:scale-125 transition" style={{ background: r.color + "15" }} data-testid={`react-${r.key}-${post.post_id}`}>
                  <r.icon className="h-5 w-5" style={{ color: r.color }} fill="currentColor" />
                </button>
              ))}
            </div>
          )}
        </div>
        <button onClick={() => setShowComments((s) => !s)} className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100 flex items-center gap-2" data-testid={`comment-btn-${post.post_id}`}>
          <MessageCircle className="h-4 w-4" />Commenter
        </button>
        <button className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100 flex items-center gap-2">
          <Share2 className="h-4 w-4" />Partager
        </button>
        <button onClick={() => setReportOpen(true)} className="px-3 py-2 rounded-lg text-xs font-semibold text-slate-400 hover:text-red-600 hover:bg-red-50 flex items-center gap-1" data-testid={`report-btn-${post.post_id}`} aria-label="Signaler">
          <Flag className="h-3.5 w-3.5" />
        </button>
      </footer>

      <ReportPostDialog open={reportOpen} onOpenChange={setReportOpen} postId={post.post_id} />

      {showComments && (
        <div className="bg-slate-50 border-t border-slate-100 px-5 py-3 space-y-2">
          {comments.length === 0 && <div className="text-xs text-slate-400">Aucun commentaire — soyez le premier.</div>}
          {comments.map((c) => {
            const isVerifiedPro = c.author?.blue_check && (c.author?.role === "doctor" || c.author?.role === "pharmacy");
            return (
              <div key={c.comment_id} className="flex gap-2 items-start">
                <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-700 flex-shrink-0">{(c.author?.name || "?").charAt(0)}</div>
                <div className="flex-1 bg-white border border-slate-200 rounded-2xl px-3 py-1.5">
                  <div className="flex items-center gap-1 text-xs font-bold text-slate-800">
                    {c.author?.name}{c.author?.blue_check && <VerifiedBadge size={10} />}
                  </div>
                  {/* Tag "Réponse vérifiée RPPS [Spécialité]" — US-14 CDC v1.0 */}
                  {isVerifiedPro && (
                    <div className="inline-flex items-center gap-1 mt-0.5 mb-1 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-full px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide" data-testid={`verified-reply-${c.comment_id}`}>
                      <CheckCircle2 className="h-2.5 w-2.5" />
                      Réponse vérifiée RPPS{c.author?.specialty ? ` · ${c.author.specialty}` : ""}
                    </div>
                  )}
                  <div className="text-sm text-slate-700">{c.content}</div>
                </div>
              </div>
            );
          })}
          <form onSubmit={submitComment} className="flex gap-2 items-center pt-2">
            <input value={commentText} onChange={(e) => setCommentText(e.target.value)} placeholder="Écrire un commentaire…"
                   className="flex-1 bg-white border border-slate-200 rounded-full px-4 py-2 text-sm focus:outline-none focus:border-[#618DB8]" data-testid={`comment-input-${post.post_id}`} />
            <button type="submit" disabled={!commentText.trim()} className="text-[#618DB8] disabled:text-slate-300" data-testid={`comment-send-${post.post_id}`}>
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      )}
    </article>
  );
}

export default function Feed() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isPro = ["doctor", "pharmacy"].includes(user?.role);
  const [posts, setPosts] = useState([]);
  const [text, setText] = useState("");
  const [postType, setPostType] = useState(isPro ? "article" : "qa");
  const [qaDisclaimerAck, setQaDisclaimerAck] = useState(false);
  const [imgFile, setImgFile] = useState(null);
  const [imgPreview, setImgPreview] = useState(null);
  const [posting, setPosting] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [activeTag, setActiveTag] = useState("");
  const [showClinicalCase, setShowClinicalCase] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    setActiveTag(sp.get("tag") || "");
  }, []);

  // Has any Q&A in current feed?
  const hasQA = posts.some((p) => p.type === "qa");

  const load = async () => {
    try {
      const params = {};
      if (searchQ.trim()) params.q = searchQ.trim();
      if (activeTag) params.tag = activeTag;
      const { data } = await api.get("/feed", { params });
      setPosts(data);
    }
    catch (e) { toast.error(formatErr(e?.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeTag]);
  const onSearch = (e) => { e.preventDefault(); load(); };

  const publish = async (e) => {
    e.preventDefault();
    if (!text.trim() && !imgFile) return;
    if (postType === "qa" && !qaDisclaimerAck) {
      return toast.error("Vous devez accepter la mention légale avant de publier votre question.");
    }
    setPosting(true);
    try {
      let image_url = null;
      if (imgFile) {
        const fd = new FormData();
        fd.append("file", imgFile);
        const { data } = await api.post("/posts/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        image_url = data.url;
      }
      await api.post("/posts", {
        content: text,
        type: postType,
        image_url,
        medical_disclaimer_acknowledged: postType === "qa" ? true : undefined,
      });
      setText(""); setImgFile(null); setImgPreview(null); setQaDisclaimerAck(false);
      load();
      toast.success(postType === "qa" ? "Question publiée — un professionnel vous répondra" : "Publication envoyée");
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || e.message); }
    finally { setPosting(false); }
  };

  const onImage = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setImgFile(f);
    const r = new FileReader(); r.onload = () => setImgPreview(r.result); r.readAsDataURL(f);
  };

  const react = async (postId, reactionKey) => {
    try {
      const { data } = await api.post(`/posts/${postId}/react`, { reaction: reactionKey });
      setPosts((ps) => ps.map((p) => {
        if (p.post_id !== postId) return p;
        const counts = { ...(p.reactions_count || {}) };
        const prev = p.my_reaction;
        if (prev) counts[prev] = Math.max(0, (counts[prev] || 0) - 1);
        if (data.reaction) counts[data.reaction] = (counts[data.reaction] || 0) + 1;
        const total = Object.values(counts).reduce((a, b) => a + b, 0);
        return { ...p, my_reaction: data.reaction, reactions_count: counts, reactions_total: total };
      }));
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || e.message); }
  };

  return (
    <div className="p-4 lg:p-8 max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-deep">{t("feed.title")}</h1>
        <p className="text-sm text-slate-500 mt-1">Restez connecté à votre communauté santé.</p>
        <form onSubmit={onSearch} className="mt-4 flex gap-2">
          <input value={searchQ} onChange={(e) => setSearchQ(e.target.value)} placeholder="Rechercher dans le fil (mot-clé, sujet…)" className="flex-1 bg-white border border-slate-200 rounded-full px-4 py-2 text-sm focus:outline-none focus:border-[#618DB8]" data-testid="feed-search-input" />
          <Button type="submit" className="btn-primary rounded-full px-5" data-testid="feed-search-btn">Chercher</Button>
        </form>
        {activeTag && (
          <div className="mt-3 inline-flex items-center gap-2 bg-[#618DB8]/10 text-[#618DB8] rounded-full px-3 py-1 text-sm font-semibold">
            #{activeTag}
            <button onClick={() => { window.history.replaceState({}, "", "/dashboard/feed"); setActiveTag(""); }} className="hover:text-[#FF7A5C]" data-testid="clear-tag-btn">×</button>
          </div>
        )}
      </div>

      {/* Stories 24h bar */}
      <StoriesBar />

      {/* Opt-in notifications push (pros uniquement) */}
      <PushOptInBanner />

      {/* Composer */}
      <form onSubmit={publish} className="bg-white rounded-2xl border border-slate-200 p-4 mb-6 shadow-sm">
        <div className="flex gap-3">
          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#618DB8] to-[#2A4365] text-white flex items-center justify-center font-bold flex-shrink-0">
            {(user?.name || "?").charAt(0).toUpperCase()}
          </div>
          <div className="flex-1">
            {/* Type selector */}
            <div className="flex gap-1.5 mb-3 flex-wrap">
              {isPro ? (
                <>
                  <button type="button" onClick={() => setPostType("article")} className={`text-xs px-3 py-1.5 rounded-full font-semibold flex items-center gap-1 transition ${postType === "article" ? "bg-[#618DB8] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid="post-type-article">
                    <FileText className="h-3 w-3" />Article
                  </button>
                  <button type="button" onClick={() => setPostType("alert")} className={`text-xs px-3 py-1.5 rounded-full font-semibold flex items-center gap-1 transition ${postType === "alert" ? "bg-red-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`} data-testid="post-type-alert">
                    <ShieldAlert className="h-3 w-3" />Alerte santé
                  </button>
                  <button type="button" onClick={() => { setShowClinicalCase(true); }} className="text-xs px-3 py-1.5 rounded-full font-semibold flex items-center gap-1 bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition" data-testid="post-type-clinical-case">
                    <Stethoscope className="h-3 w-3" />Cas clinique…
                  </button>
                </>
              ) : (
                <button type="button" onClick={() => setPostType("qa")} className={`text-xs px-3 py-1.5 rounded-full font-semibold flex items-center gap-1 ${postType === "qa" ? "bg-blue-600 text-white" : "bg-blue-50 text-blue-700 border border-blue-200"}`} data-testid="post-type-qa">
                  <HelpCircle className="h-3 w-3" />Question santé
                </button>
              )}
            </div>

            <Textarea value={text} onChange={(e) => setText(e.target.value)}
              placeholder={postType === "qa" ? "Posez votre question santé — un professionnel vérifié vous répondra…" : t("feed.compose_placeholder")}
              className="border-0 shadow-none focus-visible:ring-0 resize-none p-0 text-base" rows={2} data-testid="feed-compose-input" />

            {imgPreview && (
              <div className="relative mt-2 inline-block">
                <img src={imgPreview} alt="" className="max-h-48 rounded-lg border border-slate-200" />
                <button type="button" onClick={() => { setImgFile(null); setImgPreview(null); if (fileRef.current) fileRef.current.value = ""; }}
                        className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 hover:bg-black/80">
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}

            {/* Q&A medical disclaimer consent (US-13 CDC) */}
            {postType === "qa" && (
              <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 text-xs text-amber-900 flex items-start gap-2">
                <input
                  type="checkbox"
                  id="qa-disclaimer-ack"
                  checked={qaDisclaimerAck}
                  onChange={(e) => setQaDisclaimerAck(e.target.checked)}
                  className="mt-0.5 accent-amber-600"
                  data-testid="qa-disclaimer-ack"
                />
                <label htmlFor="qa-disclaimer-ack" className="cursor-pointer leading-snug">
                  J'ai compris que <strong>cet échange ne constitue pas un avis médical personnalisé</strong> et n'engage pas la responsabilité des professionnels qui y répondent. En cas d'urgence, j'appelle le 15 (SAMU).
                </label>
              </div>
            )}

            <div className="flex items-center justify-between mt-2 pt-3 border-t border-slate-100">
              <input type="file" ref={fileRef} accept="image/*" onChange={onImage} className="hidden" data-testid="feed-image-input" />
              <button type="button" onClick={() => fileRef.current?.click()} className="text-slate-400 hover:text-[#618DB8] transition flex items-center gap-1.5 text-sm font-semibold" data-testid="feed-add-image-btn">
                <ImageIcon className="h-5 w-5" />Photo
              </button>
              <Button type="submit" disabled={posting || (!text.trim() && !imgFile) || (postType === "qa" && !qaDisclaimerAck)} className="btn-coral rounded-full px-6" data-testid="feed-publish-btn">
                {posting ? "…" : t("feed.publish")}
              </Button>
            </div>
          </div>
        </div>
      </form>

      <ClinicalCaseDialog open={showClinicalCase} onOpenChange={setShowClinicalCase} onPublished={load} />

      {posts.length === 0 ? (
        <div className="text-center py-12 text-slate-500 border border-dashed border-slate-300 rounded-xl">{t("feed.no_posts")}</div>
      ) : (
        <div className="space-y-4 pb-20">
          {posts.map((p) => <PostCard key={p.post_id} post={p} onReact={react} onComment={load} />)}
        </div>
      )}

      {/* Bannière SAMU fixe — affichée dès qu'une Q&A est visible (US-13 CDC v1.0 §5) */}
      {hasQA && <EmergencyBanner />}
    </div>
  );
}
